import React from 'react';
import { usePageEditor } from './PageEditorContext';
import { 
  Edit3, Save, Eye, X, Loader2, AlertTriangle, 
  Check, Mail, HelpCircle
} from 'lucide-react';

const EditModeToolbar = () => {
  const { 
    isEditMode, 
    isAdmin, 
    toggleEditMode, 
    hasUnsavedChanges,
    isSaving,
    saveAsDraft,
    publishChanges,
    discardChanges,
    message,
    currentPage
  } = usePageEditor();

  const handleExit = () => {
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard them and exit?')) return;
    discardChanges();
    window.location.href = '/admin';
  };

  // Don't show if not admin
  if (!isAdmin) return null;

  // Floating edit button when not in edit mode
  if (!isEditMode) {
    return (
      <button
        onClick={toggleEditMode}
        data-testid="edit-page-btn"
        className="fixed bottom-6 right-6 z-50 bg-[#E91E8C] hover:bg-[#d11a7d] text-white px-5 py-3 rounded-full shadow-xl flex items-center gap-2 font-semibold transition-all hover:scale-105"
        title="Edit this page"
      >
        <Edit3 className="w-5 h-5" />
        Edit Page
      </button>
    );
  }

  // Full toolbar when in edit mode
  return (
    <>
      {/* Top toolbar */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-[#E91E8C] to-[#d11a7d] text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5" />
                <span className="font-bold">Edit Mode</span>
              </div>
              {currentPage && (
                <span className="text-white/70 text-sm capitalize">
                  Editing: {currentPage.replace('-', ' ')}
                </span>
              )}
              {hasUnsavedChanges && (
                <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                  Unsaved changes
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Hover help card */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm transition-colors"
                  data-testid="edit-help-trigger"
                >
                  <HelpCircle className="w-4 h-4" />
                  How to edit
                </button>
                <div className="absolute right-0 top-full mt-2 w-80 bg-white text-gray-800 rounded-xl shadow-2xl border border-gray-100 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[120]" data-testid="edit-help-card">
                  <p className="font-bold text-sm mb-2 text-[#E91E8C]">How to edit this page</p>
                  <ol className="text-xs text-gray-600 space-y-1.5 list-decimal pl-4">
                    <li><strong>Hover</strong> over any text — editable text glows pink with a pencil icon.</li>
                    <li><strong>Click</strong> the text and type your change. Press <strong>Enter</strong> (or the green tick) to keep it, <strong>Esc</strong> to cancel.</li>
                    <li><strong>Save Draft</strong> keeps your changes private — visitors won't see them yet.</li>
                    <li><strong>Publish</strong> makes all your changes live on the website immediately.</li>
                    <li><strong>Exit</strong> returns you to the admin panel.</li>
                  </ol>
                  <p className="text-xs text-gray-400 mt-3 pt-2 border-t border-gray-100">
                    The header menu and footer are site-wide — editing them here changes every page.
                    Stuck? <a href="mailto:hello@audioandco.com?subject=Website%20Edit%20Help" className="text-[#E91E8C] hover:underline">Email us</a>.
                  </p>
                </div>
              </div>

              {/* Save Draft */}
              <button
                onClick={saveAsDraft}
                disabled={isSaving || !hasUnsavedChanges}
                data-testid="save-draft-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:opacity-50 rounded-lg font-medium transition-colors"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Draft
              </button>

              {/* Publish */}
              <button
                onClick={publishChanges}
                disabled={isSaving}
                data-testid="publish-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white text-[#E91E8C] hover:bg-gray-100 disabled:opacity-50 rounded-lg font-bold transition-colors"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                Publish
              </button>

              {/* Exit back to admin panel */}
              <button
                onClick={handleExit}
                data-testid="exit-edit-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
              >
                <X className="w-4 h-4" />
                Exit
              </button>
            </div>
          </div>
        </div>

        {/* Warning banner */}
        <div className="bg-amber-500 text-amber-900 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>You are editing the live website.</strong> Click on any highlighted text to edit. 
              Save Draft to keep changes private, or Publish to make them live.
            </span>
          </div>
        </div>
      </div>

      {/* Spacer to push content below fixed toolbar */}
      <div className="h-[100px]" />

      {/* Message toast */}
      {message && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Instruction overlay for first time */}
      <style>{`
        [data-editable="true"]:hover {
          outline: 2px dashed #E91E8C;
          outline-offset: 4px;
        }
      `}</style>
    </>
  );
};

export default EditModeToolbar;
